import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import authHeader from "../../UserServices/auth-header";
import { NoteState } from "../../context/notes/NoteState";
import "../Reports/ActiveClientsReport.css";

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "1", label: "Gym" },
  { value: "2", label: "Personal Trainer" },
  { value: "3", label: "Guest Pass" },
  { value: "4", label: "Small Group PT" },
  { value: "5", label: "Nutrition" },
  { value: "6", label: "Registration" },
  { value: "7", label: "Boot Camp" },
  { value: "9", label: "General" },
  { value: "10", label: "Cafe Sales" },
  { value: "11", label: "Vostro Fitness Academy" },
  { value: "12", label: "Massage Chair" },
  { value: "13", label: "Cafe Deposit" },
  { value: "14", label: "Physiotherapy" },
  { value: "15", label: "GX" },
  { value: "16", label: "Befit" },
];

const DETAIL_TABS = [
  { key: "overview", label: "Overview", icon: "bi-person-badge" },
  { key: "packages", label: "All Packages", icon: "bi-box-seam" },
  { key: "active", label: "Active Packages", icon: "bi-check-circle" },
  { key: "history", label: "Package History", icon: "bi-clock-history" },
  { key: "payments", label: "Payments", icon: "bi-credit-card" },
  { key: "gym", label: "Gym Check-ins", icon: "bi-door-open" },
  { key: "sessions", label: "PT Sessions", icon: "bi-calendar-check" },
  { key: "freezing", label: "Freezing", icon: "bi-snow" },
  { key: "cards", label: "Access Cards", icon: "bi-credit-card-2-front" },
];

const fmtDate = (value) =>
  value && value !== "0000-00-00" ? moment(value).format("DD MMM YYYY") : "—";

const filterClientsForPicker = (term, source) => {
  const list = source || [];
  const value = (term || "").trim().toLowerCase();
  if (!value) {
    return list;
  }
  return list.filter(
    (client) =>
      client.name.toLowerCase().includes(value) ||
      (client.phone && client.phone.includes(value)) ||
      (client.uid && String(client.uid).toLowerCase().includes(value))
  );
};

const riskBadge = (risk) => {
  const map = {
    critical: { cls: "acr-risk acr-risk--critical", text: "≤ 1 month" },
    warning: { cls: "acr-risk acr-risk--warning", text: "≤ 2 months" },
    expired: { cls: "acr-risk acr-risk--expired", text: "Expired" },
    ok: { cls: "acr-risk acr-risk--ok", text: "OK" },
  };
  const item = map[risk] || map.ok;
  return <span className={item.cls}>{item.text}</span>;
};

const SummaryStat = ({ label, value }) => (
  <div className="acr-summary-card static">
    <div className="acr-summary-num">{value}</div>
    <div className="acr-summary-label">{label}</div>
  </div>
);

export default function ClientDetailsReport() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(() => String(branch_id || ""));
  const [allClients, setAllClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientLabel, setSelectedClientLabel] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    category: "",
    package_status: "",
    sale_type: "",
  });

  useEffect(() => {
    setSelectedBranchId(String(branch_id || ""));
    setSelectedClientId("");
    setSelectedClientLabel("");
    setClientSearchTerm("");
    setDetail(null);
  }, [branch_id]);

  useEffect(() => {
    const handleClick = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_Link}/branches/branches-name-list`,
        { headers: authHeader(), params: { status: 1 } }
      );
      if (response?.data?.status) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadClientsForPicker = useCallback(async () => {
    if (!selectedBranchId) {
      setAllClients([]);
      setFilteredClients([]);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_Link}/clients/client-name`,
        { headers: authHeader(), params: { branch_id: selectedBranchId } }
      );
      if (response?.data?.status) {
        const mapped = (response.data.data || []).map((client) => ({
          id: client.id,
          uid: client.uid,
          name: `${client.first_name || ""} ${client.last_name || ""}`.trim(),
          phone: client.phone || "",
        }));
        setAllClients(mapped);
        setFilteredClients(mapped);
      } else {
        setAllClients([]);
        setFilteredClients([]);
      }
    } catch (error) {
      console.error(error);
      setAllClients([]);
      setFilteredClients([]);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadClientsForPicker();
    setSelectedClientId("");
    setSelectedClientLabel("");
    setClientSearchTerm("");
    setDetail(null);
  }, [loadClientsForPicker]);

  const selectedBranchLabel = useMemo(() => {
    const match = branches.find((branch) => String(branch.id) === String(selectedBranchId));
    return match?.name || "Select branch";
  }, [branches, selectedBranchId]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleBranchChange = (value) => {
    setSelectedBranchId(value);
    setSelectedClientId("");
    setSelectedClientLabel("");
    setClientSearchTerm("");
    setDetail(null);
  };

  const handleClientSearch = (event) => {
    const value = event.target.value;
    setClientSearchTerm(value);
    setShowClientDropdown(true);
    setFilteredClients(filterClientsForPicker(value, allClients));
    if (!value.trim()) {
      setSelectedClientId("");
      setSelectedClientLabel("");
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClientId(String(client.id));
    setSelectedClientLabel(client.name);
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };

  const buildDetailParams = () => ({
    start_date: filters.start_date || undefined,
    end_date: filters.end_date || undefined,
    category: filters.category || undefined,
    package_status: filters.package_status || undefined,
    sale_type: filters.sale_type || undefined,
  });

  const loadClientDetail = async () => {
    if (!selectedBranchId) {
      toast.error("Please select a branch first.");
      return;
    }
    if (!selectedClientId) {
      toast.error("Please select a client.");
      return;
    }
    if (filters.start_date && filters.end_date && Date.parse(filters.start_date) > Date.parse(filters.end_date)) {
      toast.error("End date must be on or after start date.");
      return;
    }

    setLoading(true);
    setDetail(null);
    setDetailTab("overview");

    try {
      const response = await UserServices.getActiveClientDetail(selectedClientId, buildDetailParams());
      if (response?.data?.status) {
        setDetail(response.data.data);
      } else {
        toast.error(response?.data?.message || "Could not load client details.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load client details.");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    if (!detail?.profile) {
      return;
    }

    setExporting(true);
    try {
      const profile = detail.profile;
      const doc = new jsPDF("p", "mm", "a4");
      let y = 14;

      doc.setFontSize(16);
      doc.text("Client Details Report", 14, y);
      y += 8;
      doc.setFontSize(10);
      doc.text(`Branch: ${selectedBranchLabel}`, 14, y);
      y += 5;
      doc.text(`Client: ${profile.full_name} (${profile.uid || profile.id})`, 14, y);
      y += 5;
      doc.text(`Generated: ${moment().format("DD MMM YYYY HH:mm")}`, 14, y);
      y += 8;

      doc.autoTable({
        startY: y,
        head: [["Field", "Value"]],
        body: [
          ["Phone", profile.phone || "—"],
          ["Email", profile.email || "—"],
          ["Gender", profile.gender || "—"],
          ["Joining Date", fmtDate(profile.joining_date || profile.registration_date)],
          ["Registration Package", profile.registration_package || "—"],
          ["Address", profile.address || "—"],
          ["CNIC", profile.cnic || "—"],
        ],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [232, 0, 43] },
      });

      y = doc.lastAutoTable.finalY + 8;

      const packageRows = (detail.all_packages || []).map((pkg) => [
        pkg.package_name,
        pkg.category,
        pkg.sale_type || "—",
        fmtDate(pkg.sale_date),
        fmtDate(pkg.start_date),
        fmtDate(pkg.end_date),
        pkg.price,
        pkg.discount,
        pkg.net_price,
        pkg.is_active ? "Active" : "Inactive",
      ]);

      doc.autoTable({
        startY: y,
        head: [["Package", "Category", "Sale Type", "Sale Date", "Start", "End", "Price", "Discount", "Net", "Status"]],
        body: packageRows,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [232, 0, 43] },
      });

      doc.save(`client-details-${profile.uid || profile.id}-${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (error) {
      console.error(error);
      toast.error("PDF export failed.");
    } finally {
      setExporting(false);
    }
  };

  const exportCSV = () => {
    if (!detail?.profile) {
      return;
    }

    setExporting(true);
    try {
      const profile = detail.profile;
      let csv = "Client Details Report\n";
      csv += `Branch,${selectedBranchLabel}\n`;
      csv += `Client,${profile.full_name}\n`;
      csv += `UID,${profile.uid || ""}\n`;
      csv += `Phone,${profile.phone || ""}\n`;
      csv += `Email,${profile.email || ""}\n`;
      csv += `Joining Date,${fmtDate(profile.joining_date || profile.registration_date)}\n\n`;
      csv += "Package,Category,Sale Type,Sale Date,Start,End,Price,Discount,Net Price,Status,Trainer,Sessions\n";

      (detail.all_packages || []).forEach((pkg) => {
        csv += `"${pkg.package_name}","${pkg.category}","${pkg.sale_type || ""}","${fmtDate(pkg.sale_date)}","${fmtDate(pkg.start_date)}","${fmtDate(pkg.end_date)}",${pkg.price || 0},${pkg.discount || 0},${pkg.net_price || 0},"${pkg.is_active ? "Active" : "Inactive"}","${pkg.trainer_name || ""}","${pkg.sessions_delivered ?? ""}/${pkg.sessions_total ?? ""}"\n`;
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `client-details-${profile.uid || profile.id}-${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error(error);
      toast.error("Excel export failed.");
    } finally {
      setExporting(false);
    }
  };

  const renderPackageTable = (rows, emptyText) => {
    if (!rows?.length) {
      return <p className="text-muted mb-0">{emptyText}</p>;
    }

    return (
      <div className="table-responsive">
        <table className="table table-sm acr-table">
          <thead>
            <tr>
              <th>Package</th>
              <th>Category</th>
              <th>Sale Type</th>
              <th>Sale Date</th>
              <th>Start</th>
              <th>End</th>
              <th>Price</th>
              <th>Discount</th>
              <th>Net</th>
              <th>Trainer</th>
              <th>Sessions</th>
              <th>Status</th>
              <th>Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((pkg) => (
              <tr key={pkg.order_detail_id}>
                <td>{pkg.package_name}</td>
                <td>{pkg.category}</td>
                <td>{pkg.sale_type || "—"}</td>
                <td>{fmtDate(pkg.sale_date)}</td>
                <td>{fmtDate(pkg.start_date)}</td>
                <td>{fmtDate(pkg.end_date)}</td>
                <td>{pkg.price ?? "—"}</td>
                <td>{pkg.discount ?? "—"}</td>
                <td>{pkg.net_price ?? "—"}</td>
                <td>{pkg.trainer_name || "—"}</td>
                <td>
                  {pkg.sessions_total != null
                    ? `${pkg.sessions_delivered}/${pkg.sessions_total}`
                    : "—"}
                </td>
                <td>{pkg.is_active ? "Active" : "Inactive"}</td>
                <td>{riskBadge(pkg.expiry_risk)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = () => {
    if (loading) {
      return <div className="text-center py-5"><Spinner animation="border" /></div>;
    }
    if (!detail?.profile) {
      return <p className="text-muted text-center py-5 mb-0">Select branch and client, then click Load Report.</p>;
    }

    const profile = detail.profile;

    switch (detailTab) {
      case "overview":
        return (
          <>
            <div className="acr-detail-grid mb-4">
              <div className="acr-detail-card">
                <h6>Contact</h6>
                <dl>
                  <dt>UID</dt><dd>{profile.uid || "—"}</dd>
                  <dt>Phone</dt><dd>{profile.phone || "—"}</dd>
                  <dt>Email</dt><dd>{profile.email || "—"}</dd>
                  <dt>Gender</dt><dd>{profile.gender || "—"}</dd>
                  <dt>Branch</dt><dd>{profile.branch_name || selectedBranchLabel}</dd>
                  <dt>Client Type</dt><dd>{profile.type || "—"}</dd>
                </dl>
              </div>
              <div className="acr-detail-card">
                <h6>Membership</h6>
                <dl>
                  <dt>Joining Date</dt><dd>{fmtDate(profile.joining_date || profile.registration_date)}</dd>
                  <dt>Registration Package</dt><dd>{profile.registration_package || "—"}</dd>
                  <dt>Registration Date</dt><dd>{fmtDate(profile.registration_date)}</dd>
                  <dt>CNIC</dt><dd>{profile.cnic || "—"}</dd>
                  <dt>Address</dt><dd>{profile.address || "—"}</dd>
                </dl>
              </div>
              <div className="acr-detail-card">
                <h6>Summary</h6>
                <dl>
                  <dt>Total Packages</dt><dd>{detail.counts?.total_packages ?? 0}</dd>
                  <dt>Active Packages</dt><dd>{detail.counts?.active_packages ?? 0}</dd>
                  <dt>History Packages</dt><dd>{detail.counts?.history_packages ?? 0}</dd>
                  <dt>Total Price</dt><dd>Rs {detail.counts?.total_price ?? 0}</dd>
                  <dt>Total Discount</dt><dd>Rs {detail.counts?.total_discount ?? 0}</dd>
                  <dt>Total Net</dt><dd>Rs {detail.counts?.total_net_price ?? 0}</dd>
                </dl>
              </div>
            </div>
            {renderPackageTable(detail.all_packages || [], "No packages found for selected filters.")}
          </>
        );
      case "packages":
        return renderPackageTable(detail.all_packages || [], "No packages found.");
      case "active":
        return renderPackageTable(detail.active_packages || [], "No active packages found.");
      case "history":
        return renderPackageTable(detail.package_history || [], "No package history found.");
      case "payments":
        return (
          <div className="table-responsive">
            <table className="table table-sm acr-table">
              <thead><tr><th>Date</th><th>Order</th><th>Amount</th><th>Method</th><th>Status</th><th>Note</th></tr></thead>
              <tbody>
                {(detail.payments || []).length ? (
                  detail.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{fmtDate(payment.date)}</td>
                      <td>{payment.order_id}</td>
                      <td>{payment.received}</td>
                      <td>{payment.payment_method || "—"}</td>
                      <td>{payment.payment_status || "—"}</td>
                      <td>{payment.note || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6" className="text-center text-muted">No payments found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      case "gym":
        return (
          <div className="table-responsive">
            <table className="table table-sm acr-table">
              <thead><tr><th>Date</th><th>Check In</th><th>Check Out</th><th>Status</th></tr></thead>
              <tbody>
                {(detail.gym_attendance || []).length ? (
                  detail.gym_attendance.map((row) => (
                    <tr key={row.id}>
                      <td>{fmtDate(row.date)}</td>
                      <td>{row.checkin_time_12h || row.checkin_time_24h || "—"}</td>
                      <td>{row.checkout_time_12h || row.checkout_time_24h || "—"}</td>
                      <td>{row.attendance_status || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className="text-center text-muted">No gym check-ins found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      case "sessions":
        return (
          <div className="table-responsive">
            <table className="table table-sm acr-table">
              <thead><tr><th>Date</th><th>Package</th><th>Trainer</th><th>Valid</th></tr></thead>
              <tbody>
                {(detail.session_attendance || []).length ? (
                  detail.session_attendance.map((row) => (
                    <tr key={row.id}>
                      <td>{fmtDate(row.date)}</td>
                      <td>{row.package_name}</td>
                      <td>{row.trainer_name || "—"}</td>
                      <td>{row.validate_status === "1" ? "Yes" : "No"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className="text-center text-muted">No PT sessions found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      case "freezing":
        return (
          <div className="table-responsive">
            <table className="table table-sm acr-table">
              <thead><tr><th>Start</th><th>End</th><th>Reason</th></tr></thead>
              <tbody>
                {(detail.freezing || []).length ? (
                  detail.freezing.map((row) => (
                    <tr key={row.id}>
                      <td>{fmtDate(row.start_date)}</td>
                      <td>{fmtDate(row.end_date)}</td>
                      <td>{row.reason || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-center text-muted">No freezing records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      case "cards":
        return (
          <div className="table-responsive">
            <table className="table table-sm acr-table">
              <thead><tr><th>Card #</th><th>Status</th><th>Created</th></tr></thead>
              <tbody>
                {(detail.cards || []).length ? (
                  detail.cards.map((row) => (
                    <tr key={row.id}>
                      <td>{row.number}</td>
                      <td>{row.status}</td>
                      <td>{fmtDate(row.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-center text-muted">No access cards found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="wrapper active-clients-report">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
        <div className="card acr-card">
          <div className="card-body">
            <div className="acr-header">
              <div>
                <MenuButton />
                <h4 className="d-inline-block ms-2 mb-0">Client Details Report</h4>
                <p className="text-muted small mb-0 mt-1">
                  Select branch, search client, then view complete package and activity history.
                  <span className="acr-branch-pill ms-2"><i className="bi bi-building me-1" />{selectedBranchLabel}</span>
                </p>
              </div>
              <div className="acr-export-btns">
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={exportPDF} disabled={exporting || !detail}>
                  {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-file-pdf me-1" />}PDF
                </button>
                <button type="button" className="btn btn-outline-success btn-sm" onClick={exportCSV} disabled={exporting || !detail}>
                  {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-file-earmark-spreadsheet me-1" />}Excel
                </button>
              </div>
            </div>

            <div className="acr-filters">
              <div className="row g-2 acr-filter-row">
                <div className="col-12 col-sm-6 col-lg-2">
                  <label className="acr-filter-label">Branch <span className="text-danger">*</span></label>
                  <select className="form-select form-select-sm acr-branch-select" value={selectedBranchId} onChange={(e) => handleBranchChange(e.target.value)}>
                    <option value="">Select branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={String(branch.id)}>{branch.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <label className="acr-filter-label">Client <span className="text-danger">*</span></label>
                  <div className="position-relative acr-client-input-wrap" ref={clientDropdownRef}>
                    <div className="input-group input-group-sm acr-filter-control">
                      <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type name, phone or UID..."
                        value={clientSearchTerm}
                        onChange={handleClientSearch}
                        onFocus={() => {
                          setShowClientDropdown(true);
                          setFilteredClients(filterClientsForPicker(clientSearchTerm, allClients));
                        }}
                        autoComplete="off"
                        disabled={!selectedBranchId}
                      />
                      {clientSearchTerm ? (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setClientSearchTerm("");
                            setSelectedClientId("");
                            setSelectedClientLabel("");
                          }}
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    {showClientDropdown && filteredClients.length > 0 ? (
                      <div className="acr-client-dropdown">
                        <div className="acr-client-dropdown-meta">{filteredClients.length} of {allClients.length} clients</div>
                        {filteredClients.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            className={`acr-client-dropdown-item${String(client.id) === String(selectedClientId) ? " active" : ""}`}
                            onClick={() => handleClientSelect(client)}
                          >
                            <span className="acr-client-dropdown-name">{client.name}</span>
                            <span className="acr-client-dropdown-sub">
                              {[client.uid ? `UID: ${client.uid}` : null, client.phone].filter(Boolean).join(" · ")}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {showClientDropdown && clientSearchTerm && filteredClients.length === 0 ? (
                      <div className="acr-client-dropdown acr-client-dropdown-empty">No clients match</div>
                    ) : null}
                  </div>
                  {selectedClientLabel ? <small className="text-muted">Selected: {selectedClientLabel}</small> : null}
                </div>

                <div className="col-6 col-sm-4 col-lg-2">
                  <label className="acr-filter-label">Start Date</label>
                  <input className="form-control form-control-sm" type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} />
                </div>

                <div className="col-6 col-sm-4 col-lg-2">
                  <label className="acr-filter-label">End Date</label>
                  <input className="form-control form-control-sm" type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} />
                </div>

                <div className="col-12 col-sm-4 col-lg-3">
                  <label className="acr-filter-label">Category</label>
                  <select className="form-select form-select-sm" name="category" value={filters.category} onChange={handleFilterChange}>
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-sm-4 col-lg-3">
                  <label className="acr-filter-label">Package Status</label>
                  <select className="form-select form-select-sm" name="package_status" value={filters.package_status} onChange={handleFilterChange}>
                    <option value="">All packages</option>
                    <option value="active">Active only</option>
                    <option value="history">History only</option>
                  </select>
                </div>

                <div className="col-6 col-sm-4 col-lg-3">
                  <label className="acr-filter-label">Sale Type</label>
                  <select className="form-select form-select-sm" name="sale_type" value={filters.sale_type} onChange={handleFilterChange}>
                    <option value="">All</option>
                    <option value="New">New</option>
                    <option value="Renew">Renew</option>
                  </select>
                </div>

                <div className="col-6 col-sm-4 col-lg-2 d-flex align-items-end">
                  <button type="button" className="btn btn-primary btn-sm w-100" onClick={loadClientDetail} disabled={loading}>
                    {loading ? "Loading..." : "Load Report"}
                  </button>
                </div>
              </div>
            </div>

            {detail?.counts ? (
              <div className="acr-summary-row">
                <SummaryStat label="Packages" value={detail.counts.total_packages} />
                <SummaryStat label="Active" value={detail.counts.active_packages} />
                <SummaryStat label="Payments" value={detail.counts.payments} />
                <SummaryStat label="Gym Check-ins" value={detail.counts.gym_checkins} />
                <SummaryStat label="PT Sessions" value={detail.counts.sessions_delivered} />
                <SummaryStat label="Total Price" value={`Rs ${detail.counts.total_price}`} />
                <SummaryStat label="Total Discount" value={`Rs ${detail.counts.total_discount}`} />
                <SummaryStat label="Total Net" value={`Rs ${detail.counts.total_net_price}`} />
              </div>
            ) : null}

            {detail ? (
              <div className="acr-view-tabs">
                {DETAIL_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`acr-view-tab ${detailTab === tab.key ? "active" : ""}`}
                    onClick={() => setDetailTab(tab.key)}
                  >
                    <i className={`bi ${tab.icon} me-1`} />{tab.label}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="mt-3">{renderTabContent()}</div>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}
